import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UNIT_PRECISIONS } from "@/lib/units";

/**
 * The columns every unit row shares — what it governs, the unit, the decimal
 * places, a sample — so the four rows line up whether a commodity offers two
 * units or five. On a narrow screen the label sits above the two pickers and
 * the sample below them; one line from `sm` up.
 */
export const UNIT_ROW_GRID =
  "grid grid-cols-[minmax(0,1fr)_5rem] items-center gap-x-3 gap-y-2 sm:grid-cols-[4.5rem_9.5rem_5.5rem_minmax(0,1fr)] sm:gap-x-4";

/**
 * One unit preference in Preferences → Units: what it governs (Rate, Usage),
 * the unit, the decimal places (0 to the maximum), and a sample value drawn
 * in the pick. Every pick saves straight to the session, so the sample
 * updates the moment either control changes. `commodity` names the controls
 * for assistive tech, since each card repeats the same row labels.
 */
export function UnitRow<U extends string>({
  commodity,
  label,
  units,
  unit,
  onUnitChange,
  precision,
  onPrecisionChange,
  preview,
  disabled,
}: {
  commodity: string;
  label: string;
  units: readonly { code: U; label: string }[];
  unit: U;
  onUnitChange: (unit: U) => void;
  precision: number;
  onPrecisionChange: (precision: number) => void;
  preview: string;
  disabled: boolean;
}) {
  const name = `${commodity} ${label.toLowerCase()}`;
  return (
    <div className={`${UNIT_ROW_GRID} py-3 first:pt-0 last:pb-0`}>
      <span className="col-span-2 text-sm font-medium text-foreground sm:col-span-1">{label}</span>

      <Select value={unit} onValueChange={(next) => onUnitChange(next as U)} disabled={disabled}>
        <SelectTrigger aria-label={`${name} unit`} className="tabular-nums">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {units.map((u) => (
            <SelectItem key={u.code} value={u.code} className="tabular-nums">
              {u.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={String(precision)}
        onValueChange={(next) => onPrecisionChange(Number(next))}
        disabled={disabled}
      >
        <SelectTrigger aria-label={`${name} decimals`} className="tabular-nums">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {UNIT_PRECISIONS.map((p) => (
            <SelectItem key={p} value={String(p)} className="tabular-nums">
              {/* A string: Radix mirrors the chosen item's text into the trigger,
                  and a numeric 0 is falsy there, so 0 decimals showed blank. */}
              {String(p)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <output
        aria-label={`${name} preview`}
        className="col-span-2 truncate text-right text-sm font-semibold tabular-nums text-foreground sm:col-span-1"
      >
        {preview}
      </output>
    </div>
  );
}
